import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getUser as fetchCurrentUser,
  logout as logoutRequest,
  type AuthUser,
} from "../api/auth";
import { setFormUserId } from "../api/client";
import { setUnauthorizedHandler } from "../api/unauthorized";
import {
  canCreateForms,
  canManageForm,
  canManageOrganisations,
  canManageOrgMembers,
  canUseManagementTools,
  canViewForm,
  type FormGrantAccess,
} from "../lib/auth/formAccess";
import {
  clearIdentityRedirectFlag,
  getIdentityLoginUrl,
  markIdentityRedirectAttempted,
} from "../lib/auth/identity";
import type { UserOrganisation } from "../types/organisation";

type AuthContextValue = {
  roles: string[];
  organisations: UserOrganisation[];
  grantedFormIds: number[];
  manageGrantedFormIds: number[];
  user: AuthUser | null;
  authError: string | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  canManageOrganisations: boolean;
  canCreateForms: boolean;
  canUseManagementTools: boolean;
  canManageForm: (
    organisationId: number | null | undefined,
    access?: FormGrantAccess,
  ) => boolean;
  canViewForm: (
    organisationId: number | null | undefined,
    access?: FormGrantAccess,
  ) => boolean;
  canManageOrgMembers: (organisationId: number) => boolean;
  getOrganisationName: (
    organisationId: number | null | undefined,
  ) => string | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<string[]>([]);
  const [organisations, setOrganisations] = useState<UserOrganisation[]>([]);
  const [grantedFormIds, setGrantedFormIds] = useState<number[]>([]);
  const [manageGrantedFormIds, setManageGrantedFormIds] = useState<number[]>(
    [],
  );
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isBootstrapping, setBootstrapping] = useState(true);

  const clearAuth = useCallback(() => {
    setFormUserId(null);
    setRoles([]);
    setOrganisations([]);
    setGrantedFormIds([]);
    setManageGrantedFormIds([]);
    setUser(null);
    setAuthError(null);
  }, []);

  const handleUnauthorized = useCallback(() => {
    const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    markIdentityRedirectAttempted();
    clearAuth();
    navigate("/login", { replace: true, state: { from } });
  }, [clearAuth, navigate]);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  useEffect(() => {
    let cancelled = false;

    void fetchCurrentUser({ skipUnauthorizedHandler: true })
      .then((response) => {
        if (cancelled) return;
        setUser(response.user);
        setRoles(response.roles);
        setOrganisations(response.organisations);
        setGrantedFormIds(response.grantedFormIds);
        setManageGrantedFormIds(response.manageGrantedFormIds);
        setAuthError(response.formsAccessError ?? null);
        clearIdentityRedirectFlag();
      })
      .catch(() => {
        if (!cancelled) clearAuth();
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clearAuth]);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // Локальное состояние всё равно очищается.
    } finally {
      clearAuth();
      clearIdentityRedirectFlag();
      const returnUrl = `${window.location.origin}/`;
      window.location.assign(getIdentityLoginUrl(returnUrl));
    }
  }, [clearAuth]);

  const hasRole = useCallback((role: string) => roles.includes(role), [roles]);

  const getOrganisationName = useCallback(
    (organisationId: number | null | undefined) => {
      if (organisationId == null) return null;
      return (
        organisations.find((org) => org.id === organisationId)?.name ?? null
      );
    },
    [organisations],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      roles,
      organisations,
      grantedFormIds,
      manageGrantedFormIds,
      user,
      authError,
      isAuthenticated: user != null,
      isBootstrapping,
      logout,
      hasRole,
      canManageOrganisations: canManageOrganisations(roles),
      canCreateForms: canCreateForms(roles, organisations),
      canUseManagementTools: canUseManagementTools(
        roles,
        organisations,
        grantedFormIds,
      ),
      canManageForm: (organisationId, access) =>
        canManageForm(roles, organisations, organisationId, {
          ...access,
          grantedFormIds: access?.grantedFormIds ?? grantedFormIds,
          manageGrantedFormIds:
            access?.manageGrantedFormIds ?? manageGrantedFormIds,
        }),
      canViewForm: (organisationId, access) =>
        canViewForm(roles, organisations, organisationId, {
          ...access,
          grantedFormIds: access?.grantedFormIds ?? grantedFormIds,
          manageGrantedFormIds:
            access?.manageGrantedFormIds ?? manageGrantedFormIds,
        }),
      canManageOrgMembers: (organisationId) =>
        canManageOrgMembers(roles, organisations, organisationId),
      getOrganisationName,
    }),
    [
      roles,
      organisations,
      grantedFormIds,
      manageGrantedFormIds,
      user,
      authError,
      isBootstrapping,
      logout,
      hasRole,
      getOrganisationName,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
